import type { ReactNode } from "react";
import { UserPlus, X } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { relationshipService } from "@project/services/admin/relationship.service";
import {
  REFERENCE_SOURCE_OPTIONS,
  newFamilyMemberRow,
  type VisitorFamilyMemberFormRow,
} from "@project/types/visitor";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";
import { useI18n } from "@project/lib/use-i18n";

export type VisitorFormFieldsVariant = "full" | "inquiry";

type VisitorFormFieldsProps = {
  editing: Record<string, unknown> | null;
  setEditing: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >;
  /** `inquiry` shows Visit Details, Reason for Visit, and Reference only. */
  variant?: VisitorFormFieldsVariant;
};

function patch(
  editing: Record<string, unknown> | null,
  setEditing: VisitorFormFieldsProps["setEditing"],
  updates: Record<string, unknown>
) {
  setEditing({ ...(editing ?? {}), ...updates });
}

function getFamilyMembers(
  editing: Record<string, unknown> | null
): VisitorFamilyMemberFormRow[] {
  return Array.isArray(editing?.family_members)
    ? (editing.family_members as VisitorFamilyMemberFormRow[])
    : [];
}

async function searchRelationships(query: string) {
  const result = await relationshipService.listRelationshipsPage({
    page: 1,
    page_size: 50,
    search: query || undefined,
  });
  return result.data
    .filter(r => r.is_active)
    .map(r => ({
      value: String(r.id),
      label: r.relationship_name,
      secondary: r.relationship_code,
    }));
}

function seed(id: string, name: string) {
  return id && name ? { value: id, label: name } : undefined;
}

function calculateAgeFromDob(dobStr: string): number | undefined {
  if (!dobStr) return undefined;
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 ? age : undefined;
}

export function VisitorFormFields({
  editing,
  setEditing,
  variant = "full",
}: VisitorFormFieldsProps) {
  const { t } = useI18n();
  const isInquiry = variant === "inquiry";
  const family = getFamilyMembers(editing);
  const hasVisitedBefore = Boolean(editing?.has_visited_before);

  const setField = (key: string, value: unknown) => {
    patch(editing, setEditing, { [key]: value });
  };

  const updateFamily = (
    rowId: string,
    updates: Partial<VisitorFamilyMemberFormRow>
  ) => {
    setEditing(prev => ({
      ...(prev ?? {}),
      family_members: getFamilyMembers(prev).map(row =>
        row.rowId === rowId ? { ...row, ...updates } : row
      ),
    }));
  };

  const addFamilyMember = () => {
    setEditing(prev => ({
      ...(prev ?? {}),
      family_members: [
        ...getFamilyMembers(prev),
        newFamilyMemberRow(getFamilyMembers(prev).length + 1),
      ],
    }));
  };

  const removeFamilyMember = (rowId: string) => {
    setEditing(prev => ({
      ...(prev ?? {}),
      family_members: getFamilyMembers(prev)
        .filter(row => row.rowId !== rowId)
        .map((row, index) => ({ ...row, sr_no: index + 1 })),
    }));
  };

  return (
    <div className="space-y-6">
      <Section title={t("account.visitorFields.sections.visitDetails")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t("account.visitorFields.labels.visitDate")}
            type="date"
            value={String(editing?.date ?? "")}
            onChange={v => setField("date", v)}
            required
          />
          <Field
            label={t("account.visitorFields.labels.visitorName")}
            value={String(editing?.name ?? "")}
            onChange={v => setField("name", v)}
            required
          />
          <Field
            label={t("account.visitorFields.labels.dateOfBirth")}
            type="date"
            value={String(editing?.date_of_birth ?? "")}
            onChange={v => {
              const computedAge = calculateAgeFromDob(v);
              patch(editing, setEditing, {
                date_of_birth: v,
                age: computedAge,
              });
            }}
          />
          <Field
            label={t("account.visitorFields.labels.age")}
            type="number"
            value={editing?.age !== undefined && editing?.age !== null ? String(editing.age) : ""}
            readOnly
            placeholder={t("account.visitorFields.labels.agePlaceholder")}
          />
          <SelectLike label={t("account.visitorFields.labels.marriedStatus")}>
            <Select
              value={editing?.married_status ? "true" : "false"}
              onValueChange={v => {
                const isMarried = v === "true";
                patch(editing, setEditing, {
                  married_status: isMarried,
                  marriage_anniversary_date: isMarried
                    ? String(editing?.marriage_anniversary_date ?? "")
                    : "",
                });
              }}
            >
              <SelectTrigger className="rounded-xl border-input bg-background">
                <SelectValue placeholder={t("account.visitorFields.labels.marriedStatusPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">{t("account.visitorFields.labels.unmarriedOrSingle")}</SelectItem>
                <SelectItem value="true">{t("account.visitorFields.labels.married")}</SelectItem>
              </SelectContent>
            </Select>
          </SelectLike>
          {Boolean(editing?.married_status) ? (
            <Field
              label={t("account.visitorFields.labels.marriageAnniversaryDate")}
              type="date"
              value={String(editing?.marriage_anniversary_date ?? "")}
              onChange={v => setField("marriage_anniversary_date", v)}
            />
          ) : null}
          <Field
            label={t("account.visitorFields.labels.occupation")}
            value={String(editing?.occupation ?? "")}
            onChange={v => setField("occupation", v)}
          />
          <div>
            <Label>{t("account.visitorFields.labels.mobileNumber")}</Label>
            <MobileNumberField
              value={String(editing?.mobile_number ?? "")}
              country={
                (editing?.mobile_country as PhoneCountryCode | undefined) ??
                DEFAULT_PHONE_COUNTRY
              }
              onCountryChange={code => setField("mobile_country", code)}
              onChange={v => setField("mobile_number", v)}
              inputClassName="rounded-xl border-input bg-background"
            />
          </div>
          <Field
            label={t("account.visitorFields.labels.residencePhone")}
            type="tel"
            value={String(editing?.residence_phone ?? "")}
            onChange={v => setField("residence_phone", v)}
          />
          <Field
            label={t("account.visitorFields.labels.email")}
            type="email"
            value={String(editing?.email ?? "")}
            onChange={v => setField("email", v)}
          />
        </div>
        <TextField
          label={t("account.visitorFields.labels.address")}
          value={String(editing?.address ?? "")}
          onChange={v => setField("address", v)}
        />
        <TextField
          label={t("account.visitorFields.labels.businessAddress")}
          value={String(editing?.business_address ?? "")}
          onChange={v => setField("business_address", v)}
        />
      </Section>

      <Section title={t("account.visitorFields.sections.reasonForVisit")}>
        <TextField
          label={t("account.visitorFields.labels.reasonForVisit")}
          value={String(editing?.reason_for_visit ?? "")}
          onChange={v => setField("reason_for_visit", v)}
        />
        <div className="bg-muted/30 flex items-center justify-between rounded-xl border border-border px-4 py-3">
          <div>
            <Label>{t("account.visitorFields.labels.visitedBefore")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("account.visitorFields.labels.visitedBeforeHelp")}
            </p>
          </div>
          <Switch
            checked={hasVisitedBefore}
            onCheckedChange={checked => {
              patch(editing, setEditing, {
                has_visited_before: checked,
                previous_visit_details: checked
                  ? String(editing?.previous_visit_details ?? "")
                  : "",
              });
            }}
          />
        </div>
        {hasVisitedBefore ? (
          <TextField
            label={t("account.visitorFields.labels.previousVisitDetails")}
            value={String(editing?.previous_visit_details ?? "")}
            onChange={v => setField("previous_visit_details", v)}
          />
        ) : null}
      </Section>

      <Section title={t("account.visitorFields.sections.reference")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectLike label={t("account.visitorFields.labels.referenceSource")}>
            <Select
              value={String(editing?.reference_source ?? "") || undefined}
              onValueChange={v => setField("reference_source", v)}
            >
              <SelectTrigger className="rounded-xl border-input bg-background">
                <SelectValue placeholder={t("account.visitorFields.labels.selectSourcePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {REFERENCE_SOURCE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(`account.visitorFields.referenceSources.${option.value}`, { defaultValue: option.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SelectLike>
          <Field
            label={t("account.visitorFields.labels.referrerName")}
            value={String(editing?.referrer_name ?? "")}
            onChange={v => setField("referrer_name", v)}
          />
          <div>
            <Label>{t("account.visitorFields.labels.referrerMobile")}</Label>
            <MobileNumberField
              value={String(editing?.referrer_mobile ?? "")}
              country={
                (editing?.referrer_mobile_country as
                  | PhoneCountryCode
                  | undefined) ?? DEFAULT_PHONE_COUNTRY
              }
              onCountryChange={code =>
                setField("referrer_mobile_country", code)
              }
              onChange={v => setField("referrer_mobile", v)}
              inputClassName="rounded-xl border-input bg-background"
            />
          </div>
        </div>
      </Section>

      {!isInquiry ? (
        <>
          <Section title={t("account.visitorFields.sections.familySummary")}>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label={t("account.visitorFields.labels.totalFamilyMembers")}
                type="number"
                value={String(editing?.total_family_members ?? "")}
                onChange={v =>
                  setField(
                    "total_family_members",
                    v === "" ? undefined : Number(v)
                  )
                }
              />
              <Field
                label={t("account.visitorFields.labels.maleCount")}
                type="number"
                value={String(editing?.male_count ?? "")}
                onChange={v =>
                  setField("male_count", v === "" ? undefined : Number(v))
                }
              />
              <Field
                label={t("account.visitorFields.labels.femaleCount")}
                type="number"
                value={String(editing?.female_count ?? "")}
                onChange={v =>
                  setField("female_count", v === "" ? undefined : Number(v))
                }
              />
            </div>
          </Section>

          <Section title={`${t("account.visitorFields.sections.familyMembers")} (${family.length})`}>
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addFamilyMember}
                className="gap-1 rounded-xl"
              >
                <UserPlus className="h-3 w-3" /> {t("account.visitorFields.labels.addFamilyMember")}
              </Button>
            </div>
            {family.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t("account.visitorFields.labels.noFamilyMembers")}
              </p>
            ) : (
              family.map(row => (
                <div
                  key={row.rowId}
                  className="bg-muted/20 relative space-y-3 rounded-xl border border-border p-4"
                >
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute right-1 top-1 h-7 w-7 text-destructive"
                    onClick={() => removeFamilyMember(row.rowId)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>{t("account.visitorFields.labels.srNo")} {row.sr_no}</Label>
                      <Input
                        readOnly
                        value={String(row.sr_no)}
                        className="bg-muted/40 rounded-xl border-input"
                      />
                    </div>
                    <Field
                      label={t("account.visitorFields.labels.name")}
                      value={row.name}
                      onChange={v => updateFamily(row.rowId, { name: v })}
                      required
                    />
                    <SelectLike label={t("account.visitorFields.labels.relationship")} required>
                      <SearchableSelect
                        value={row.relationship}
                        onValueChange={v =>
                          updateFamily(row.rowId, { relationship: v })
                        }
                        onSearch={searchRelationships}
                        seedOption={seed(
                          row.relationship,
                          row.relationshipName
                        )}
                        placeholder={t("account.visitorFields.labels.selectRelationship")}
                        searchPlaceholder={t("account.visitorFields.labels.searchRelationship")}
                        emptyText={t("account.visitorFields.labels.noRelationshipFound")}
                      />
                    </SelectLike>
                    <Field
                      label={t("account.visitorFields.labels.dateOfBirth")}
                      type="date"
                      value={row.date_of_birth}
                      onChange={v =>
                        updateFamily(row.rowId, { date_of_birth: v })
                      }
                    />
                    <Field
                      label={t("account.visitorFields.labels.education")}
                      value={row.education}
                      onChange={v => updateFamily(row.rowId, { education: v })}
                    />
                  </div>
                </div>
              ))
            )}
          </Section>

          <Section title={t("account.visitorFields.sections.feedback")}>
            <TextField
              label={t("account.visitorFields.labels.feedback")}
              value={String(editing?.feedback ?? "")}
              onChange={v => setField("feedback", v)}
            />
          </Section>
        </>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-muted/20 space-y-3 rounded-xl border border-border p-4">
      <h3 className="text-sm font-bold uppercase tracking-wider text-primary">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  readOnly,
  placeholder,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  type?: string;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <Label data-required={required ? "true" : undefined}>{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={e => onChange?.(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className={`rounded-xl border-input ${
          readOnly
            ? "bg-muted/60 text-muted-foreground cursor-not-allowed select-none"
            : "bg-background"
        }`}
        aria-required={required ? true : undefined}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        rows={2}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="rounded-xl border-input bg-background"
      />
    </div>
  );
}

function SelectLike({
  label,
  children,
  required,
}: {
  label: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div>
      <Label data-required={required ? "true" : undefined}>{label}</Label>
      {children}
    </div>
  );
}
